'use client'

import { useEffect, useState, useMemo, useCallback } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { AlertTriangle, ArrowDown, ArrowUp, Clock, Info, Calendar, TrendingUp, Eye, EyeOff, Activity, Zap } from 'lucide-react'
import { ESMStockDetailResponse, getESMStockDetail } from '@/lib/api/esmStocksApi'
import dynamic from 'next/dynamic'
import { ApexOptions } from 'apexcharts'

// Dynamically import ApexCharts to avoid SSR issues
const ReactApexChart = dynamic(() => import('react-apexcharts'), { ssr: false })

interface ESMStockDetailModalProps {
  isin: string
  onClose: () => void
}

export default function ESMStockDetailModal({ isin, onClose }: ESMStockDetailModalProps) {
  const [stockData, setStockData] = useState<ESMStockDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [chartDateRange, setChartDateRange] = useState<'all' | '1m' | '1w'>('all')
  const [chartTheme, setChartTheme] = useState<'light' | 'dark'>('light')
  const [showESMMarkers, setShowESMMarkers] = useState(true)
  const [showPrediction, setShowPrediction] = useState(true)
  const [showVolume, setShowVolume] = useState(true)
  const [showHoverDetails, setShowHoverDetails] = useState(true)

  // Detect theme
  useEffect(() => {
    // Check if dark mode is enabled
    const isDarkMode = document.documentElement.classList.contains('dark');
    setChartTheme(isDarkMode ? 'dark' : 'light');
    
    // Listen for theme changes
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.classList.contains('dark');
      setChartTheme(isDark ? 'dark' : 'light');
    });
    
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });
    
    return () => observer.disconnect();
  }, []);

  // Fetch stock details from API
  useEffect(() => {
    const fetchStockDetails = async () => {
      try {
        setLoading(true)
        const data = await getESMStockDetail(isin)
        setStockData(data)
      } catch (err) {
        console.error('Error fetching ESM stock details:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch stock details')
      } finally {
        setLoading(false)
      }
    }

    fetchStockDetails()
  }, [isin])

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    })
  }

  // Helper function to get predicted candle values based on historical data
  const getPredictedCandleValues = useCallback((chartData: { trade_date: string; open_price: number; high_price: number; low_price: number; close_price: number }[]): number[] => {
    if (!chartData.length) return [0, 0, 0, 0];
    
    // Calculate values based on last 5-10 candles if available
    const sampleSize = Math.min(chartData.length, 10);
    const recentCandles = chartData.slice(-sampleSize);
    
    // Calculate average values
    const avgClose = recentCandles.reduce((sum, candle) => sum + candle.close_price, 0) / sampleSize;
    const avgRange = recentCandles.reduce((sum, candle) => sum + (candle.high_price - candle.low_price), 0) / sampleSize;
    
    // Create a realistic predicted candle
    const predictedClose = avgClose;
    const predictedOpen = avgClose * (1 - 0.005); // Slightly lower than close
    const predictedHigh = predictedClose + (avgRange * 0.6);
    const predictedLow = predictedOpen - (avgRange * 0.4);
    
    return [predictedOpen, predictedHigh, predictedLow, predictedClose];
  }, []);

  const formatTransactionType = useCallback((type: string): string => {
    if (!type) return 'N/A'
    
    if (type === 'moved_up') return 'ESM 1->2'
    if (type === 'moved_down') return 'ESM 2->1'
    
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }, []);

  // Format large numbers to Indian system (with Cr, L, K)
  const formatNumberWithIndianSystem = useCallback((value: number | null | undefined): string => {
    if (typeof value !== 'number' || isNaN(value)) {
      return 'N/A'; // Handle undefined, null, NaN, or non-numeric types
    }
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 10000000) return `${sign}${(absValue / 10000000).toFixed(2)}Cr`;
    if (absValue >= 100000) return `${sign}${(absValue / 100000).toFixed(2)}L`;
    if (absValue >= 1000) return `${sign}${(absValue / 1000).toFixed(2)}K`;
    return `${sign}${absValue.toLocaleString('en-IN')}`;
  }, []);

  const getTransactionTypeStyle = (type: string) => {
    switch (type) {
      case 'entry':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'exit':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'moved_up':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'moved_down':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      default:
        return 'bg-zinc-100 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-200'
    }
  }

  // Calculate days spent in each state
  const historyWithDuration = useMemo(() => {
    if (!stockData?.esm_history_list) return []

    const sortedHistory = [...stockData.esm_history_list].sort(
      (a, b) => new Date(a.transition_date).getTime() - new Date(b.transition_date).getTime()
    )

    return sortedHistory.map((item, index, array) => {
      let daysInState = 0
      if (index < array.length - 1) {
        const currentDate = new Date(item.transition_date)
        const nextDate = new Date(array[index + 1].transition_date)
        daysInState = Math.round((nextDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24))
      } else {
        // For the most recent state, calculate days until today
        const currentDate = new Date(item.transition_date)
        const today = new Date()
        daysInState = Math.round((today.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24))
      }
      return { ...item, daysInState }
    })
  }, [stockData?.esm_history_list])

  // Calculate overall ESM lifecycle stats
  const esmLifecycle = useMemo(() => {
    if (!historyWithDuration.length) return null

    const firstEntry = historyWithDuration[0]
    const currentStateItem = historyWithDuration[historyWithDuration.length - 1]
    
    // Fix: Don't include exit transaction days in total days calculation
    const totalDays = historyWithDuration.reduce((sum, item) => {
      // Skip days in state when transaction is "exit"
      if (item.transaction_type === 'exit') return sum;
      return sum + item.daysInState;
    }, 0)
    
    const stateStats = historyWithDuration.reduce((acc, item) => {
      // Skip recording stats for exit state
      if (item.transaction_type === 'exit') return acc;
      
      const state = item.current_esm_state
      if (!acc[state]) acc[state] = 0
      acc[state] += item.daysInState
      return acc
    }, {} as Record<number, number>)

    // Count transitions based on state changes
    // First, check if there is any exit transaction in the history
    const hasExit = historyWithDuration.some(item => item.transaction_type === 'exit');
    let numberOfTransitions = 0;
    
    if (hasExit) {
      // If there's an exit, count transitions only after the last exit
      const lastExitIndex = historyWithDuration.map(item => item.transaction_type).lastIndexOf('exit');
      
      // Count state changes after the last exit
      if (lastExitIndex < historyWithDuration.length - 1) {
        for (let i = lastExitIndex + 1; i < historyWithDuration.length - 1; i++) {
          // Count each change from one state to another as a transition
          if (historyWithDuration[i].current_esm_state !== historyWithDuration[i + 1].current_esm_state) {
            numberOfTransitions++;
          }
        }
      }
    } else {
      // If there's no exit, count all transitions between different states
      for (let i = 0; i < historyWithDuration.length - 1; i++) {
        // Count each change from one state to another as a transition
        if (historyWithDuration[i].current_esm_state !== historyWithDuration[i + 1].current_esm_state) {
          numberOfTransitions++;
        }
      }
    }

    return {
      firstEntryDate: firstEntry.transition_date,
      totalDaysInESM: totalDays,
      currentState: currentStateItem.current_esm_state,
      stateStats,
      numberOfTransitions,
      daysInCurrentStage: currentStateItem.daysInState,
      currentStageEntryDate: currentStateItem.transition_date,
      currentStageType: currentStateItem.transaction_type,
    }
  }, [historyWithDuration])

  // Filter chart data based on selected date range
  const filteredChartData = useMemo(() => {
    if (!stockData?.chart_data) return []
    
    // Sort the data by date in ascending order
    const sortedData = [...stockData.chart_data].sort(
      (a, b) => new Date(a.trade_date).getTime() - new Date(b.trade_date).getTime()
    )
    
    if (chartDateRange === 'all') {
      return sortedData
    }
    
    const now = new Date()
    const cutoffDate = new Date()
    
    if (chartDateRange === '1w') {
      cutoffDate.setDate(now.getDate() - 7)
    } else if (chartDateRange === '1m') {
      cutoffDate.setMonth(now.getMonth() - 1)
    }
    
    return sortedData.filter(item => new Date(item.trade_date) >= cutoffDate)
  }, [stockData?.chart_data, chartDateRange])

  // Prepare candlestick chart data
  const chartOptions = useMemo(() => {
    if (!filteredChartData.length) return null
    
    // Prepare candlestick data (this will be the main series)
    const candleSeriesData = filteredChartData.map(dataPoint => ({
      x: new Date(dataPoint.trade_date).getTime(),
      y: [
        dataPoint.open_price,
        dataPoint.high_price,
        dataPoint.low_price,
        dataPoint.close_price
      ]
    }));

    // Create predicted candle data point for visualization
    const predictedCandlePoint = stockData?.prediction_data && showPrediction ? {
      x: new Date(stockData.prediction_data.predicted_transition_date).getTime(),
      y: getPredictedCandleValues(filteredChartData), // Make sure getPredictedCandleValues is in scope
      fillColor: stockData.prediction_data.prediction_type === 'exit' ? '#EF4444' : 
                stockData.prediction_data.prediction_type === 'moved_down' ? '#F59E0B' : '#3B82F6',
      strokeColor: stockData.prediction_data.prediction_type === 'exit' ? '#EF4444' : 
                  stockData.prediction_data.prediction_type === 'moved_down' ? '#F59E0B' : '#3B82F6',
      borderColor: '#fff',
      borderWidth: 1.5,
      opacity: 0.8,
      predicted: true
    } : null;

    const actualCandleSeriesData = predictedCandlePoint
      ? [...candleSeriesData, predictedCandlePoint]
      : candleSeriesData;

    // Prepare volume data
    const volumeSeriesRawData = filteredChartData.map(dataPoint => ({
      x: new Date(dataPoint.trade_date).getTime(),
      y: dataPoint.total_traded_qty || 0
    }));
    
    const predictedVolumePoint = stockData?.prediction_data && showPrediction && showVolume ? {
      x: new Date(stockData.prediction_data.predicted_transition_date).getTime(),
      y: 0, // Zero volume for prediction dummy point
      predicted: true
    } : null;

    // Ensure volume data is an empty array when not showing volume
    const actualVolumeSeriesData = showVolume 
      ? (predictedVolumePoint 
          ? [...volumeSeriesRawData, predictedVolumePoint] 
          : volumeSeriesRawData)
      : [];

    // Create vertical lines for stage changes - removed horizontal markers
    const stageChangeLines = showESMMarkers ? filteredChartData
      .filter(item => item.esm_data !== null)
      .map(item => ({
        x: new Date(item.trade_date).getTime(),
        borderColor: item.esm_data?.transaction_type === 'entry' ? '#10B981' : 
                  item.esm_data?.transaction_type === 'exit' ? '#EF4444' :
                  item.esm_data?.transaction_type === 'moved_up' ? '#3B82F6' : '#F59E0B',
        strokeDashArray: 5,
        opacity: 0.7,
        borderWidth: 1,
        label: {
          borderColor: item.esm_data?.transaction_type === 'entry' ? '#10B981' : 
                    item.esm_data?.transaction_type === 'exit' ? '#EF4444' :
                    item.esm_data?.transaction_type === 'moved_up' ? '#3B82F6' : '#F59E0B',
          style: {
            color: '#fff',
            background: item.esm_data?.transaction_type === 'entry' ? '#10B981' : 
                      item.esm_data?.transaction_type === 'exit' ? '#EF4444' :
                      item.esm_data?.transaction_type === 'moved_up' ? '#3B82F6' : '#F59E0B',
            fontSize: '10px',
            fontWeight: 'bold'
          },
          text: `ESM ${item.esm_data?.esm_state} - ${formatTransactionType(item.esm_data?.transaction_type || '')}`,
          position: 'top'
        }
      })) : [];

    // Add prediction data to annotations if available and enabled
    const predictionAnnotation = (stockData?.prediction_data && showPrediction) ? [{
      x: new Date(stockData.prediction_data.predicted_transition_date).getTime(),
      borderColor: stockData.prediction_data.prediction_type === 'exit' ? '#EF4444' : 
                  stockData.prediction_data.prediction_type === 'moved_down' ? '#F59E0B' : '#3B82F6',
      strokeDashArray: 5,
      borderWidth: 2,
      label: {
        borderColor: stockData.prediction_data.prediction_type === 'exit' ? '#EF4444' : 
                    stockData.prediction_data.prediction_type === 'moved_down' ? '#F59E0B' : '#3B82F6',
        style: {
          color: '#fff',
          background: stockData.prediction_data.prediction_type === 'exit' ? '#EF4444' : 
                      stockData.prediction_data.prediction_type === 'moved_down' ? '#F59E0B' : '#3B82F6',
          fontSize: '10px',
          fontWeight: 'bold'
        },
        text: `Predicted ${formatTransactionType(stockData.prediction_data.prediction_type)}`
      }
    }] : [];
    
    // Create base options for the main chart (Candlestick + Volume)
    const mainChartOptions: ApexOptions = {
      chart: {
        id: 'stock-main-chart', // Updated ID
        type: 'candlestick', // Base type, series will specify their own
        height: 400, // Increased chart height
        toolbar: {
          show: true,
          tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
          autoSelected: 'zoom' // Changed to zoom for easier in/out
        },
        background: chartTheme === 'dark' ? '#1f2937' : '#ffffff',
        animations: {
          enabled: false
        },
        zoom: {
          enabled: true,
          type: 'xy', // Enable both x and y axis zooming
          autoScaleYaxis: true
        }
      },
      theme: {
        mode: chartTheme
      },
      title: {
        text: 'Stock Price Chart',
        align: 'left',
        style: {
          color: chartTheme === 'dark' ? '#e5e7eb' : '#111827'
        }
      },
      annotations: {
        points: [], // Removed ESM event points
        xaxis: [...stageChangeLines, ...predictionAnnotation]
      },
      plotOptions: {
        candlestick: {
          colors: {
            upward: '#10B981',
            downward: '#EF4444'
          },
          wick: {
            useFillColor: true, // Use fill color for wicks
          }
        },
        bar: { // Options for Volume series
          columnWidth: '70%',
          colors: {
            ranges: [{
              from: -Number.MAX_SAFE_INTEGER, // Cover all values
              to: Number.MAX_SAFE_INTEGER,
              color: chartTheme === 'dark' ? 'rgba(99, 102, 241, 0.35)' : 'rgba(99, 102, 241, 0.25)'
            }]
          },
        }
      },
      xaxis: {
        type: 'datetime',
        labels: {
          style: {
            colors: chartTheme === 'dark' ? '#d1d5db' : '#374151'
          },
          formatter: function(val: string | number) {
            return new Date(val).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
          }
        }
      },
      yaxis: [ // Array for multiple Y-axes
        { // Primary Y-Axis (Price)
          seriesName: 'Candles',
          title: {
            text: 'Price (₹)',
            style: { color: chartTheme === 'dark' ? '#d1d5db' : '#374151' }
          },
          labels: {
            style: { colors: chartTheme === 'dark' ? '#d1d5db' : '#374151' },
            formatter: (val) => `₹${val.toFixed(2)}`
          },
          tooltip: { enabled: true }
        },
        ...(showVolume ? [{ // Secondary Y-Axis (Volume) - Conditionally added
          seriesName: 'Volume',
          opposite: true, // Position on the right
          title: {
            text: 'Volume',
            style: { color: chartTheme === 'dark' ? '#d1d5db' : '#374151' }
          },
          labels: {
            show: true,
            formatter: (val: number) => formatNumberWithIndianSystem(val),
            style: { colors: chartTheme === 'dark' ? '#d1d5db' : '#374151' },
            minWidth: 40,
            maxWidth: 80,
          },
          axisTicks: { show: true },
          axisBorder: { show: true, color: chartTheme === 'dark' ? '#374151' : '#e5e7eb' },
          tooltip: { enabled: false }, // Disable tooltip for this axis, use shared chart tooltip
          tickAmount: 4,
          forceNiceScale: true,
        }] : [])
      ],
      tooltip: {
        enabled: showHoverDetails,
        theme: chartTheme,
        shared: true, // Key for combined tooltip
        intersect: false, // Show tooltip based on x-axis hover
        custom: function({ series, seriesIndex, dataPointIndex, w }: { 
          series: Array<number[]>; 
          seriesIndex: number; 
          dataPointIndex: number; 
          w: {
            config: {
              series: Array<{
                name: string;
                type: string;
                data: Array<{
                  x: number;
                  y: number[] | number;
                  predicted?: boolean;
                  fillColor?: string;
                  strokeColor?: string;
                }>
              }>
            }
          }
        }) {
          if (!showHoverDetails) return '';
          
          // Get candle data directly from the configured series
          const candleSeriesConfig = w.config.series.find((s) => s.name === 'Candles' && s.type === 'candlestick');
          if (!candleSeriesConfig || !candleSeriesConfig.data || dataPointIndex >= candleSeriesConfig.data.length) {
            return ''; // No valid candle data to show
          }

          const currentCandleData = candleSeriesConfig.data[dataPointIndex];
          if (!currentCandleData) return '';

          // Prediction tooltip (if the candle itself is predicted)
          if (currentCandleData.predicted) {
            const dateStr = new Date(currentCandleData.x).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
            const predictionType = stockData?.prediction_data?.prediction_type || '';
            const formattedPrediction = formatTransactionType(predictionType);
            
            return `
              <div class="${chartTheme === 'dark' ? 'bg-zinc-800 text-zinc-100' : 'bg-white text-zinc-800'} p-4 rounded-lg shadow-md max-w-md">
                <div class="flex items-center mb-2">
                  <div class="w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                    predictionType === 'exit' 
                      ? 'bg-red-500 text-white' 
                      : predictionType === 'moved_down' 
                      ? 'bg-yellow-500 text-white'
                      : 'bg-blue-500 text-white'
                  }">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                    </svg>
                  </div>
                  <div class="font-bold text-lg ${chartTheme === 'dark' ? 'text-white' : 'text-zinc-900'}">Prediction: ${formattedPrediction}</div>
                </div>
                <div class="grid grid-cols-2 gap-2 my-2 text-sm">
                  <div class="font-medium">Date:</div><div>${dateStr}</div>
                  <div class="font-medium">Current Progress:</div><div>${stockData?.prediction_data?.days_completed || 0} / ${stockData?.prediction_data?.min_days_required || 0} days</div>
                  <div class="font-medium">Days Remaining:</div><div>${Math.max(0, (stockData?.prediction_data?.min_days_required || 0) - (stockData?.prediction_data?.days_completed || 0))} days</div>
                </div>
                <div class="text-xs mt-2 pt-2 border-t ${chartTheme === 'dark' ? 'border-zinc-700' : 'border-zinc-200'}">
                  ${stockData?.prediction_data?.reason || ''}
                </div>
              </div>
            `;
          }
          
          // Regular data point tooltip
          const date = new Date(currentCandleData.x).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
          const ohlc = currentCandleData.y as number[]; // Assert this is definitely a number array
          
          // Find the corresponding original data point for additional info
          const correspondingDataIndex = filteredChartData.findIndex(point => 
            new Date(point.trade_date).getTime() === currentCandleData.x
          );
          
          const correspondingOriginalData = correspondingDataIndex !== -1 
            ? filteredChartData[correspondingDataIndex] 
            : null;
          
          // Volume data handling
          let volumeHtml = '';
          if (showVolume) {
            // Find volume series and get volume data directly
            const volumeSeriesConfig = w.config.series.find((s) => s.name === 'Volume' && s.type === 'bar');
            const volumeValue = volumeSeriesConfig && volumeSeriesConfig.data && volumeSeriesConfig.data[dataPointIndex] 
              ? volumeSeriesConfig.data[dataPointIndex].y 
              : null;
            
            if (currentCandleData.predicted) {
              volumeHtml = '<div>Volume: N/A (Prediction)</div>';
            } else if (volumeValue !== null && correspondingOriginalData) {
              const volumeAsNumber = Array.isArray(volumeValue) ? volumeValue[0] : volumeValue;
              const tradedAmount = correspondingOriginalData.close_price * volumeAsNumber;
              volumeHtml = `
                <div>Volume: ${formatNumberWithIndianSystem(volumeAsNumber)}</div>
                <div>Traded Amount: ₹${formatNumberWithIndianSystem(tradedAmount)}</div>
              `;
            } else {
              volumeHtml = '<div>Volume: N/A</div>';
            }
          }
          
          // Trades info
          const tradesHtml = correspondingOriginalData && typeof correspondingOriginalData.number_of_trades === 'number'
            ? `<div>Trades: ${correspondingOriginalData.number_of_trades.toLocaleString('en-IN')}</div>`
            : '<div>Trades: N/A</div>';
          
          // ESM info
          const esmInfo = correspondingOriginalData?.esm_data && showESMMarkers
            ? `<div class="font-medium ${chartTheme === 'dark' ? 'text-green-400' : 'text-green-600'} mt-1">
                ESM State: ${correspondingOriginalData.esm_data.esm_state} - 
                ${formatTransactionType(correspondingOriginalData.esm_data.transaction_type)}
               </div>` 
            : '';

          return `
            <div class="${chartTheme === 'dark' ? 'bg-zinc-800 text-zinc-100' : 'bg-white text-zinc-800'} p-3 rounded-lg shadow-md">
              <div class="font-bold text-lg ${chartTheme === 'dark' ? 'text-white' : 'text-zinc-900'}">${date}</div>
              <div class="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                <div>Open:</div><div class="font-medium">₹${ohlc[0].toFixed(2)}</div>
                <div>High:</div><div class="font-medium">₹${ohlc[1].toFixed(2)}</div>
                <div>Low:</div><div class="font-medium">₹${ohlc[2].toFixed(2)}</div>
                <div>Close:</div><div class="font-medium">₹${ohlc[3].toFixed(2)}</div>
              </div>
              <div class="mt-2 pt-2 border-t ${chartTheme === 'dark' ? 'border-zinc-700' : 'border-zinc-200'}">
                ${volumeHtml}
                ${tradesHtml}
              </div>
              ${esmInfo}
            </div>
          `;
        }
      },
      grid: {
        borderColor: chartTheme === 'dark' ? '#374151' : '#e5e7eb',
      },
      legend: {
        labels: {
          colors: chartTheme === 'dark' ? '#e5e7eb' : '#111827'
        }
      },
      stroke: { // Define stroke widths for series: [candle, volume_bar]
        width: showVolume ? [1, 0] : [1], // Candle stroke 1px, Volume bar no stroke
        // curve: 'smooth' // If mixing with line charts, not for candle/bar
      },
      series: [
        {
          name: 'Candles',
          type: 'candlestick',
          data: actualCandleSeriesData
        },
        ...(showVolume ? [{
          name: 'Volume',
          type: 'bar',
          data: actualVolumeSeriesData,
        }] : [])
      ],
    };

    return { mainChartOptions };

  }, [filteredChartData, chartTheme, showESMMarkers, showVolume, showHoverDetails, stockData?.prediction_data, showPrediction, getPredictedCandleValues, formatNumberWithIndianSystem, formatTransactionType]);

  // Function to render timeline item with connection lines
  const renderTimelineItem = (item: typeof historyWithDuration[0], index: number, isLast: boolean) => {
    return (
      <div key={`${item.isin}-${item.transition_date}`} className="relative">
        {/* Vertical connection line */}
        {!isLast && (
          <div className="absolute top-7 left-3.5 bottom-0 w-0.5 bg-zinc-200 dark:bg-zinc-700"></div>
        )}
        
        <div className="flex items-start mb-6">
          {/* Circle indicator */}
          <div className={`flex flex-shrink-0 items-center justify-center w-7 h-7 rounded-full border-2 border-white dark:border-zinc-900 ${getTransactionTypeStyle(item.transaction_type)} z-10`}>
            {item.transaction_type === 'entry' && <ArrowUp size={14} />}
            {item.transaction_type === 'exit' && <ArrowDown size={14} />}
            {item.transaction_type === 'moved_up' && <ArrowUp size={14} />}
            {item.transaction_type === 'moved_down' && <ArrowDown size={14} />}
          </div>
          
          {/* Content */}
          <div className="ml-3 bg-white dark:bg-zinc-900 p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 w-full">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center">
                <span className="text-sm font-medium mr-2">State {item.current_esm_state}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getTransactionTypeStyle(item.transaction_type)}`}>
                  {formatTransactionType(item.transaction_type)}
                </span>
              </div>
              <div className="flex items-center text-zinc-500 dark:text-zinc-400 text-xs">
                <Calendar size={12} className="mr-1" />
                <span>{formatDate(item.transition_date)}</span>
              </div>
            </div>
            
            <div className="flex items-center mt-2 text-sm text-zinc-600 dark:text-zinc-300">
              <Clock size={14} className="mr-1.5" />
              <span>{item.daysInState} days in this state</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 w-4/5 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !stockData) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 w-4/5 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex justify-between items-center px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
            <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Error</h2>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-500 dark:hover:text-zinc-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 p-6">
            <div className="flex items-center text-red-500 mb-4">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span>Failed to load stock details</span>
            </div>
            {error && <p className="text-sm text-zinc-500 dark:text-zinc-400">{error}</p>}
          </div>
        </div>
      </div>
    )
  }

  const { stock_details, stock_financial_data } = stockData

  // Create safe fallbacks for nullable financial data
  const financialData = stock_financial_data || {
    current_price: null,
    price_to_earning: null,
    market_capitalization: null,
    promoter_holding: null
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 w-4/5 max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
            {stock_details.stock_name}
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-500 dark:hover:text-zinc-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Stock Chart */}
          {chartOptions && (
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
              <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
                <div className="flex flex-wrap items-center justify-between mb-3 gap-2">
                  <div className="flex items-center">
                    <TrendingUp size={20} className="text-emerald-600 dark:text-emerald-400 mr-2" />
                    <h3 className="text-lg font-medium text-zinc-900 dark:text-white">Price Chart</h3>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {/* Chart controls group */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-zinc-500 dark:text-zinc-400">Display:</span>
                      <button 
                        onClick={() => setShowESMMarkers(!showESMMarkers)}
                        className={`px-3 py-1 text-xs rounded-md ${
                          showESMMarkers 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                        }`}
                        title="Toggle ESM markers visibility"
                      >
                        ESM Markers
                      </button>
                      {stockData?.prediction_data && (
                        <button 
                          onClick={() => setShowPrediction(!showPrediction)}
                          className={`px-3 py-1 text-xs rounded-md ${
                            showPrediction 
                              ? 'bg-yellow-500 text-white' 
                              : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                          }`}
                          title="Toggle prediction visibility"
                        >
                          Prediction
                        </button>
                      )}
                      <button 
                        onClick={() => {
                          // Force a complete chart redraw by using key updates instead of just toggling state
                          setShowVolume(!showVolume);
                        }}
                        className={`px-3 py-1 text-xs rounded-md ${
                          showVolume 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                        }`}
                        title="Toggle volume chart visibility"
                      >
                        Volume
                      </button>
                      <button 
                        onClick={() => setShowHoverDetails(!showHoverDetails)}
                        className={`px-3 py-1 text-xs rounded-md flex items-center gap-1 ${
                          showHoverDetails 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                        }`}
                        title="Toggle hover details visibility"
                      >
                        {showHoverDetails ? <Eye size={14} /> : <EyeOff size={14} />} Hover Details
                      </button>
                    </div>
                    
                    {/* Date range filters */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-zinc-500 dark:text-zinc-400">Range:</span>
                      <button 
                        onClick={() => setChartDateRange('1w')}
                        className={`px-3 py-1 text-xs rounded-md ${
                          chartDateRange === '1w' 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                        }`}
                      >
                        Week
                      </button>
                      <button 
                        onClick={() => setChartDateRange('1m')}
                        className={`px-3 py-1 text-xs rounded-md ${
                          chartDateRange === '1m' 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                        }`}
                      >
                        Month
                      </button>
                      <button 
                        onClick={() => setChartDateRange('all')}
                        className={`px-3 py-1 text-xs rounded-md ${
                          chartDateRange === 'all' 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700'
                        }`}
                      >
                        All
                      </button>
                    </div>
                  </div>
                </div>
                {!loading && !error && chartOptions && (
                  <div className="h-[450px] flex flex-col" id="stock-chart-container">
                    {/* Combined chart container */}
                    <div className="flex-1 min-h-0">
                      <ReactApexChart
                        key={`main-stock-chart-${showVolume ? 'volume' : 'no-volume'}-${chartTheme}`}
                        options={chartOptions.mainChartOptions}
                        series={chartOptions.mainChartOptions.series}
                        type="candlestick"
                        height="100%"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Prediction Component - Moved directly below chart */}
          {stockData?.prediction_data && (
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
              <div className="bg-zinc-50 dark:bg-zinc-800/60 p-5 rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden relative">
                {/* Background Pattern */}
                <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
                  <svg className="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <pattern id="prediction-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                        <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                      </pattern>
                    </defs>
                    <rect width="100" height="100" fill="url(#prediction-grid)" />
                  </svg>
                </div>
                
                {/* Header with icon and toggle button */}
                <div className="flex items-center justify-between mb-4 relative z-10">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                      stockData.prediction_data.prediction_type === 'exit' 
                        ? 'bg-red-500 text-white' 
                        : stockData.prediction_data.prediction_type === 'moved_down'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-blue-500 text-white'
                    }`}>
                      <Zap size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Transition Prediction</h3>
                      <p className="text-sm text-zinc-600 dark:text-zinc-300">
                        {formatTransactionType(stockData.prediction_data.prediction_type)}
                      </p>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setShowPrediction(!showPrediction)}
                    className="flex items-center space-x-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200"
                    style={{
                      backgroundColor: showPrediction 
                        ? (stockData.prediction_data.prediction_type === 'exit' 
                            ? 'rgba(239, 68, 68, 0.15)' 
                            : stockData.prediction_data.prediction_type === 'moved_down'
                            ? 'rgba(245, 158, 11, 0.15)'
                            : 'rgba(59, 130, 246, 0.15)')
                        : 'rgba(209, 213, 219, 0.2)',
                      color: showPrediction
                        ? (stockData.prediction_data.prediction_type === 'exit' 
                            ? 'rgb(239, 68, 68)' 
                            : stockData.prediction_data.prediction_type === 'moved_down'
                            ? 'rgb(245, 158, 11)'
                            : 'rgb(59, 130, 246)')
                        : 'rgb(107, 114, 128)'
                    }}
                  >
                    <span>{showPrediction ? 'Shown on Chart' : 'Hidden on Chart'}</span>
                    {showPrediction ? <Eye size={12} /> : <EyeOff size={12} />}
                  </button>
                </div>
                
                {/* Main content with date and countdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
                  {/* Left column - Date and details */}
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
                    <div className="flex items-start mb-3">
                      <Calendar size={18} className={`mr-2 mt-0.5 ${
                        stockData.prediction_data.prediction_type === 'exit' 
                          ? 'text-red-500' 
                          : stockData.prediction_data.prediction_type === 'moved_down'
                          ? 'text-yellow-500'
                          : 'text-blue-500'
                      }`} />
                      <div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Predicted Date</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">
                          {formatDate(stockData.prediction_data.predicted_transition_date)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Clock size={18} className={`mr-2 mt-0.5 ${
                        stockData.prediction_data.prediction_type === 'exit' 
                          ? 'text-red-500' 
                          : stockData.prediction_data.prediction_type === 'moved_down'
                          ? 'text-yellow-500'
                          : 'text-blue-500'
                      }`} />
                      <div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Days Remaining</div>
                        <div className="text-lg font-semibold text-zinc-900 dark:text-white">
                          {Math.max(0, stockData.prediction_data.min_days_required - stockData.prediction_data.days_completed)} days
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Middle column - Progress */}
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Eligibility Progress</span>
                        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                          {stockData.prediction_data.days_completed >= stockData.prediction_data.min_days_required 
                            ? '100' 
                            : Math.round((stockData.prediction_data.days_completed / stockData.prediction_data.min_days_required) * 100)}%
                        </span>
                      </div>
                      <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2.5 overflow-hidden">
                        <div 
                          className="h-2.5 rounded-full transition-all duration-500 ease-in-out"
                          style={{
                            width: `${stockData.prediction_data.days_completed >= stockData.prediction_data.min_days_required 
                              ? 100 
                              : (stockData.prediction_data.days_completed / stockData.prediction_data.min_days_required) * 100}%`,
                            background: stockData.prediction_data.prediction_type === 'exit' 
                              ? 'rgb(239, 68, 68)' 
                              : stockData.prediction_data.prediction_type === 'moved_down'
                              ? 'rgb(245, 158, 11)'
                              : 'rgb(59, 130, 246)'
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-center mt-3">
                      <div className="bg-zinc-50 dark:bg-zinc-800/60 p-2 rounded-lg">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">Current</div>
                        <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{stockData.prediction_data.days_completed} days</div>
                      </div>
                      <div className="bg-zinc-50 dark:bg-zinc-800/60 p-2 rounded-lg">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">Required</div>
                        <div className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{stockData.prediction_data.min_days_required} days</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right column - Reason */}
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-zinc-200 dark:border-zinc-800">
                    <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2 flex items-center">
                      <Info size={16} className="mr-1.5" />
                      Prediction Details
                    </h4>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                      {stockData.prediction_data.reason}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stock Details */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left column - Basic info */}
            <div className="space-y-4">
              <div className="bg-zinc-50 dark:bg-zinc-800/60 p-4 rounded-xl">
                <h3 className="text-lg font-medium mb-3 text-zinc-900 dark:text-white">Stock Information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">ISIN</span>
                    <span className="font-medium text-zinc-900 dark:text-white">{stock_details.isin}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">BSE Code</span>
                    <span className="font-medium text-zinc-900 dark:text-white">{stock_details.bse_code || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">NSE Code</span>
                    <span className="font-medium text-zinc-900 dark:text-white">{stock_details.nse_code || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">Industry</span>
                    <span className="font-medium text-zinc-900 dark:text-white">{stock_details.industry_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">Sector</span>
                    <span className="font-medium text-zinc-900 dark:text-white">{stock_details.sector_name}</span>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-50 dark:bg-zinc-800/60 p-4 rounded-xl">
                <h3 className="text-lg font-medium mb-3 text-zinc-900 dark:text-white">Financial Data</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">Current Price</span>
                    <span className="font-medium text-zinc-900 dark:text-white">₹{financialData.current_price?.toFixed(2) ?? 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">P/E Ratio</span>
                    <span className="font-medium text-zinc-900 dark:text-white">{financialData.price_to_earning?.toFixed(2) ?? 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">Market Cap</span>
                    <span className="font-medium text-zinc-900 dark:text-white">₹{financialData.market_capitalization ?? 'N/A'} {financialData.market_capitalization ? 'Cr' : ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500 dark:text-zinc-400">Promoter Holding</span>
                    <span className="font-medium text-zinc-900 dark:text-white">{financialData.promoter_holding ? `${financialData.promoter_holding}%` : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column - ESM Summary */}
            <div className="space-y-4">
              <div className="bg-zinc-50 dark:bg-zinc-800/60 p-4 rounded-xl">
                <h3 className="text-lg font-medium mb-3 text-zinc-900 dark:text-white">ESM Summary</h3>
                
                {esmLifecycle && (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mr-3">
                        <Clock size={20} className="text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">First Entry Date</div>
                        <div className="font-medium text-zinc-900 dark:text-white">{formatDate(esmLifecycle.firstEntryDate)}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mr-3">
                        <Clock size={20} className="text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Total Days in ESM</div>
                        <div className="font-medium text-zinc-900 dark:text-white">{esmLifecycle.totalDaysInESM} days</div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mr-3">
                        <Info size={20} className="text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Current ESM Stage</div>
                        <div className="font-medium text-zinc-900 dark:text-white">
                          State {esmLifecycle.currentState} ({formatTransactionType(esmLifecycle.currentStageType)})
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mr-3">
                        <Calendar size={20} className="text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Date Entered Current Stage</div>
                        <div className="font-medium text-zinc-900 dark:text-white">{formatDate(esmLifecycle.currentStageEntryDate)}</div>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mr-3">
                        <Activity size={20} className="text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">Days in Current Stage</div>
                        <div className="font-medium text-zinc-900 dark:text-white">{esmLifecycle.daysInCurrentStage} days</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* State Distribution */}
              {esmLifecycle && Object.keys(esmLifecycle.stateStats).length > 0 && (
                <div className="bg-zinc-50 dark:bg-zinc-800/60 p-4 rounded-xl">
                  <h3 className="text-lg font-medium mb-3 text-zinc-900 dark:text-white">State Duration Analysis</h3>
                  
                  <div className="space-y-3">
                    {Object.entries(esmLifecycle.stateStats).map(([state, days]) => {
                      const percentage = esmLifecycle.totalDaysInESM > 0 ? Math.round((days / esmLifecycle.totalDaysInESM) * 100) : 0;
                      return (
                        <div key={state} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-zinc-700 dark:text-zinc-300">State {state}</span>
                            <span className="text-zinc-700 dark:text-zinc-300">{days} days ({percentage}%)</span>
                          </div>
                          <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2.5">
                            <div 
                              className="bg-emerald-600 h-2.5 rounded-full" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ESM History Timeline */}
          <div className="p-4 border-t border-zinc-200 dark:border-zinc-800">
            <h3 className="text-lg font-medium mb-4 text-zinc-900 dark:text-white">ESM History Timeline</h3>
            
            <div className="relative">
              {historyWithDuration.length > 0 ? (
                <div className="pl-4">
                  {historyWithDuration.slice().reverse().map((item, index, array) => 
                    renderTimelineItem(item, index, index === array.length - 1)
                  )}
                </div>
              ) : (
                <div className="text-center text-zinc-500 dark:text-zinc-400 py-6">
                  No ESM history available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 